import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./AuditTrailWorkspace.css";

function AuditTrailWorkspace() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [filters, setFilters] = useState({
    date_from: yearStart,
    date_to: today,
    application: "",
    user: "",
    branch: "",
    search: "",
  });
  const [data, setData] = useState({
    records: [],
    summary: {},
    applications: [],
    users: [],
    branches: [],
  });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== "")
      );
      const response = await axiosInstance.get("audit-trail/", { params });
      setData(response.data);
      setSelected((current) =>
        response.data.records.find((item) => item.key === current?.key) ||
        null
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Could not load the Audit Trail."
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportFile = (type) => {
    const params = new URLSearchParams({
      ...Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== "")
      ),
      export: type,
    });
    window.open(
      `${axiosInstance.defaults.baseURL}audit-trail/?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section className="audit-page">
      <header className="audit-title">
        <div>
          <span>GENERAL ADMINISTRATION</span>
          <h1>Audit Trail</h1>
          <p>
            Immutable security, policy, finance and operational activity.
          </p>
        </div>
        <div className="audit-export-actions">
          <button onClick={() => exportFile("csv")}>
            <i className="bi bi-file-earmark-spreadsheet" /> Download CSV
          </button>
          <button className="primary" onClick={() => exportFile("pdf")}>
            <i className="bi bi-printer" /> Print PDF
          </button>
        </div>
      </header>

      <div className="audit-summary">
        <article><span>Records</span><strong>{data.summary?.total_records || 0}</strong></article>
        <article><span>Applications</span><strong>{data.summary?.applications || 0}</strong></article>
        <article><span>Users</span><strong>{data.summary?.users || 0}</strong></article>
        <article><span>Branches</span><strong>{data.summary?.branches || 0}</strong></article>
      </div>

      <div className="audit-filters">
        <label>From<input type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters,date_from:e.target.value})}/></label>
        <label>To<input type="date" value={filters.date_to} onChange={(e)=>setFilters({...filters,date_to:e.target.value})}/></label>
        <label>Application<select value={filters.application} onChange={(e)=>setFilters({...filters,application:e.target.value})}>
          <option value="">All applications</option>
          {(data.applications||[]).map((item)=><option key={item}>{item}</option>)}
        </select></label>
        <label>User<select value={filters.user} onChange={(e)=>setFilters({...filters,user:e.target.value})}>
          <option value="">All users</option>
          {(data.users||[]).map((item)=><option key={item.id} value={item.id}>{item.email}</option>)}
        </select></label>
        <label>Branch<select value={filters.branch} onChange={(e)=>setFilters({...filters,branch:e.target.value})}>
          <option value="">All permitted branches</option>
          {(data.branches||[]).map((item)=><option key={item.id} value={item.id}>{item.code} · {item.name}</option>)}
        </select></label>
        <label className="audit-search">Search<input value={filters.search} onChange={(e)=>setFilters({...filters,search:e.target.value})} onKeyDown={(e)=>e.key==="Enter"&&load()} placeholder="Entity, narrative, user or value"/></label>
        <button onClick={load} disabled={busy}>{busy ? "Filtering..." : "Filter records"}</button>
      </div>

      <div className="audit-layout">
        <div className="audit-table">
          <table>
            <thead><tr>
              <th>Audit ID</th><th>Entity</th><th>Operation Narrative</th>
              <th>Value</th><th>Application</th><th>Date</th>
              <th>Time</th><th>User</th><th>Branch</th>
            </tr></thead>
            <tbody>
              {(data.records||[]).map((item)=>(
                <tr key={item.key} className={selected?.key===item.key?"active":""} onClick={()=>setSelected(item)}>
                  <td>{item.key}</td>
                  <td><strong>{item.entity_id}</strong><small>{item.entity_type}</small></td>
                  <td>{item.narrative}</td><td>{item.value}</td>
                  <td>{item.application}</td><td>{item.operation_date}</td>
                  <td>{item.operation_time}</td><td>{item.operation_user}</td>
                  <td>{item.branch}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.records?.length && <div className="audit-empty">No audit records match the selected filters.</div>}
        </div>

        {selected && (
          <aside className="audit-detail">
            <header>
              <div><span>AUDIT RECORD</span><h2>{selected.key}</h2></div>
              <button onClick={()=>setSelected(null)}>×</button>
            </header>
            <div className="audit-detail-meta">
              {[
                ["Entity", `${selected.entity_type} · ${selected.entity_id}`],
                ["Application", selected.application],
                ["User", selected.operation_user],
                ["Branch", selected.branch],
                ["Date and time", `${selected.operation_date} ${selected.operation_time}`],
                ["Value", selected.value],
              ].map(([label,value])=><article key={label}><span>{label}</span><strong>{value}</strong></article>)}
            </div>
            <section><h3>Operation narrative</h3><p>{selected.narrative}</p></section>
            <section><h3>Technical detail</h3><pre>{JSON.stringify(selected.details || {}, null, 2)}</pre></section>
          </aside>
        )}
      </div>
    </section>
  );
}
export default AuditTrailWorkspace;
